import { useRef, useEffect, useCallback } from 'react';

const DRAG_THRESHOLD = 5;

interface UseSimpleDragToCloseOptions {
  onClose: () => void;
}

const isValidDragTarget = (target: Element): boolean => {
  const modal = target.closest('[class*="modalContainer"]');
  const headerElement = target.closest('header');
  return !!(modal && headerElement);
};

export const useSimpleDragToClose = ({ onClose }: UseSimpleDragToCloseOptions) => {
  const startY = useRef(0);
  const isDragging = useRef(false);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    const target = e.target as Element;

    if (!isValidDragTarget(target)) return;

    startY.current = e.touches[0].clientY;
    isDragging.current = true;
  }, []);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isDragging.current) return;

    const currentY = e.touches[0].clientY;
    const deltaY = currentY - startY.current;

    if (deltaY >= DRAG_THRESHOLD) {
      e.preventDefault();
    }
  }, []);

  const handleTouchEnd = useCallback(
    (e: TouchEvent) => {
      if (!isDragging.current) return;

      const endY = e.changedTouches[0].clientY;
      const deltaY = endY - startY.current;

      isDragging.current = false;

      if (deltaY >= DRAG_THRESHOLD) {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);
};
